import * as timeutil from '../../../util/timeutil';
import { getSlotBoundsForDate } from '../../../util/slotTimer';

/**
 * Parent self-marking, as the school configured it:
 *   off    – parents only see attendance
 *   pa     – Present / Absent
 *   now    – Check in / out, always at the current time
 *   custom – Check in / out, at a time the parent picks (never later than now)
 */
export type ParentMarkSetting = 'off' | 'pa' | 'now' | 'custom';

export type SessionState = 'PENDING' | 'CHECKED_IN' | 'PRESENT' | 'ABSENT';

export type NextAction = 'NONE' | 'MARK' | 'CHECKIN' | 'CHECKOUT';

export interface ParticipantEvent {
    bookingId: string;
    centerId: string;
    batchId: string;
    batchName?: string;
    packageId: string;
    profileId: string;
    accountId?: string;
    date: number;
    startTime: number;
    endTime: number;
    slotRandomId: string;
    bookingStatus?: string;
}

export interface ParentSession {
    key: string;
    event: ParticipantEvent;
    start: number;
    end: number;
    attendance: Record<string, any> | null;
    state: SessionState;
}

export const getParentMarkSetting = (settings: Record<string, any> | null | undefined): ParentMarkSetting => {
    if (!settings?.enableSelfMarking) return 'off';
    if (settings.type !== 'checkin_checkout') return 'pa';
    return settings.selfMarkingTimeMode === 'custom' ? 'custom' : 'now';
};

/** Same key the attendance records are stored under. */
export const getAttendanceKey = (event: ParticipantEvent): string =>
    `${timeutil.inFormat(event.date, 'DD-MM-YY')}--${event.slotRandomId}--${event.centerId}--${event.profileId}`;

export const getSessionState = (attendance: Record<string, any> | null | undefined): SessionState => {
    if (!attendance || attendance.clear === true) return 'PENDING';
    if (attendance.absent === true) return 'ABSENT';
    if (typeof attendance.latestCheckIn === 'number' && typeof attendance.latestCheckOut !== 'number') return 'CHECKED_IN';
    if (typeof attendance.latestCheckOut === 'number' || attendance.absent === false) return 'PRESENT';
    return 'PENDING';
};

/** A cancelled booking only reaches the parent when it already has attendance; it is view-only. */
export const isCancelledEvent = (event: ParticipantEvent | null | undefined): boolean => event?.bookingStatus === 'CANCELLED';

/**
 * What a parent can do next. Every mark is made once and only on today's sessions; anything
 * already recorded (by the parent or by staff) is final for parents.
 */
export const getNextAction = (state: SessionState, setting: ParentMarkSetting, isToday: boolean, cancelled = false): NextAction => {
    if (setting === 'off' || !isToday || cancelled) return 'NONE';
    if (setting === 'pa') return state === 'PENDING' ? 'MARK' : 'NONE';
    if (state === 'PENDING') return 'CHECKIN';
    if (state === 'CHECKED_IN') return 'CHECKOUT';
    return 'NONE';
};

export const buildSessions = (
    events: ParticipantEvent[] | null | undefined,
    attendanceMap: Map<string, any> | null | undefined,
): ParentSession[] => (events || [])
    .map((event) => {
        const key = getAttendanceKey(event);
        const attendance = attendanceMap?.get(key) || null;
        const { start, end } = getSlotBoundsForDate(event);
        return { key, event, start, end, attendance, state: getSessionState(attendance) };
    })
    .sort((a, b) => a.start - b.start || a.key.localeCompare(b.key));

export interface TodayGroups {
    live: ParentSession[];
    needs: ParentSession[];
    upcoming: ParentSession[];
    settled: ParentSession[];
}

/**
 * Today ordered by what the parent has to do: what is on now, what ended without being
 * finished, what is next, and what is done. With marking off, ended sessions are just listed.
 */
export const groupTodaySessions = (sessions: ParentSession[], now: number, setting: ParentMarkSetting): TodayGroups => {
    const groups: TodayGroups = { live: [], needs: [], upcoming: [], settled: [] };
    sessions.forEach((session) => {
        if (session.start <= now && now <= session.end) groups.live.push(session);
        else if (session.start > now) groups.upcoming.push(session);
        else if (setting === 'off' || getNextAction(session.state, setting, true, isCancelledEvent(session.event)) !== 'NONE') groups.needs.push(session);
        else groups.settled.push(session);
    });
    return groups;
};

/** The range a parent may pick for a custom time: same day, never later than now, check-out not before check-in. */
export const getTimeBounds = (session: ParentSession, field: 'in' | 'out', now: number) => {
    const dayStart = timeutil.getInstance(session.event.date).startOf('day').valueOf();
    const checkIn = session.attendance?.latestCheckIn;
    const min = field === 'out' && typeof checkIn === 'number' ? checkIn : dayStart;
    return { min, max: Math.max(min, now) };
};

export const clampTime = (value: number, bounds: { min: number; max: number }) =>
    Math.min(bounds.max, Math.max(bounds.min, value));

export type TimeError = 'FUTURE' | 'BEFORE_CHECK_IN';

/**
 * The time a parent picked (hour and minute only), placed on the session's day. Picking the
 * check-in minute itself means the check-in time, so it never lands a few seconds before it.
 */
export const toSessionTime = (session: ParentSession, field: 'in' | 'out', hours: number, minutes: number): number => {
    const value = timeutil.getInstance(session.event.date).hours(hours).minutes(minutes).seconds(0).milliseconds(0).valueOf();
    const checkIn = session.attendance?.latestCheckIn;
    if (field === 'out' && typeof checkIn === 'number' && timeutil.getInstance(checkIn).isSame(timeutil.getInstance(value), 'minute')) {
        return checkIn;
    }
    return value;
};

/** Why a picked time can't be saved: it is in the future, or a check-out before the recorded check-in. */
export const getTimeError = (session: ParentSession, field: 'in' | 'out', value: number, now: number): TimeError | null => {
    if (value > now) return 'FUTURE';
    const checkIn = session.attendance?.latestCheckIn;
    if (field === 'out' && typeof checkIn === 'number' && value < checkIn) return 'BEFORE_CHECK_IN';
    return null;
};

export interface DaySummary {
    toDo: number;
    done: number;
    absent: number;
}

export const getDaySummary = (sessions: ParentSession[], setting: ParentMarkSetting, isToday: boolean): DaySummary => ({
    toDo: sessions.filter((s) => getNextAction(s.state, setting, isToday, isCancelledEvent(s.event)) !== 'NONE').length,
    done: sessions.filter((s) => s.state === 'PRESENT' || s.state === 'CHECKED_IN').length,
    absent: sessions.filter((s) => s.state === 'ABSENT').length,
});

/** Stable child colours: the n-th child of the account always gets the same tint. */
export const CHILD_TINTS = [
    { fg: '#7413BD', bg: '#F3E8FC' },
    { fg: '#0A7D5A', bg: '#E3F5EE' },
    { fg: '#B4370E', bg: '#FDECE4' },
    { fg: '#1D5FBF', bg: '#E5EEFB' },
    { fg: '#9A5800', bg: '#FDF3E1' },
];

export const getInitials = (name: string | null | undefined): string => (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';
